import { useEffect, useRef, useCallback } from 'react'
import { trackEvent } from '@/shared/api/telemetry'
// Per-lead time tracking, and the `lead_opened` signal for the Focus queue.
//
// Two gaps this closes. First, `lead_opened` was only emitted from the Table and
// Map views — Focus, where BDRs spend their day, emitted nothing with a lead id
// on it, so "which lead is this person on" was unanswerable for the main working
// surface. Second, nothing anywhere recorded time spent on a *lead*: the only
// duration in the system was `view_dwell`, which measures whole views
// (focus/table/map) and cannot tell one lead from another.
//
// Deliberately measured rather than inferred. Diffing consecutive `lead_opened`
// timestamps looks like it would work and does not: the last lead of a session
// has no successor, a lead left open over lunch reads as an hour of engagement,
// and two browser tabs interleave into nonsense. This accumulates only
// tab-visible time and flushes a real number.
//
// Mirrors useViewTracking's guards on purpose — same flush cadence, same 1s floor
// and 1h ceiling, same "never count hidden time" rule — so the two durations are
// comparable and one implementation's lessons are not lost on the other.
const FLUSH_MS = 30_000
const MIN_SEGMENT_MS = 1000
const MAX_SEGMENT_MS = 60 * 60 * 1000

export function useLeadTracking(leadId: string | null, stage?: string): void {
  const leadRef = useRef<string | null>(leadId)
  const stageRef = useRef<string | undefined>(stage)
  const startRef = useRef<number>(Date.now())
  // Whether the segment currently accruing is tab-visible time. This is what
  // decides whether a flush emits — not the visibility state at flush time,
  // which is already 'hidden' by the time the hide/pagehide handlers run and
  // would drop exactly the segments those handlers exist to bank.
  const countingRef = useRef<boolean>(document.visibilityState === 'visible')

  // Bank the time accrued on whichever lead is currently open. Reads the lead
  // from a ref so the callback stays stable across re-renders and the interval
  // below never needs re-registering.
  const flush = useCallback(() => {
    const now = Date.now()
    const ms = now - startRef.current
    startRef.current = now
    const id = leadRef.current
    if (id && countingRef.current && ms >= MIN_SEGMENT_MS && ms < MAX_SEGMENT_MS) {
      trackEvent('lead_dwell', { ms, stage: stageRef.current }, id)
    }
  }, [])

  useEffect(() => {
    stageRef.current = stage
  }, [stage])

  // Lead change: bank the old lead's time, then record the new one as opened.
  // Both matter — the dwell answers "how long", the open answers "on what, and
  // in what order".
  useEffect(() => {
    if (leadRef.current === leadId) return
    flush()
    leadRef.current = leadId
    startRef.current = Date.now()
    if (leadId) trackEvent('lead_opened', { from: 'focus', stage }, leadId)
    // `stage` is intentionally not a dependency: a stage change on the SAME lead
    // is not a new open, and re-firing on it would inflate the open count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, flush])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush()
        countingRef.current = false
      } else {
        countingRef.current = true
        startRef.current = Date.now() // resume timing fresh
      }
    }
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') flush()
    }, FLUSH_MS)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', flush)
    return () => {
      flush()
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', flush)
    }
  }, [flush])
}
